// frontend/src/components/EmployeeAttendanceCalendar.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Divider,
  LinearProgress,
  Badge,
  Zoom,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  CheckCircle,
  Cancel,
  Warning,
  AccessTime,
  EventNote,
  TrendingUp,
  HolidayVillage,
  WorkOff,
  Schedule,
  LocationOn,
  CalendarMonth,
  BarChart,
  CalendarToday,
  DateRange
} from '@mui/icons-material';
import EmployeeAttendanceRequest from './EmployeeAttendanceRequest';
import { styled } from '@mui/material/styles';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Styled components
const CalendarContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease'
}));

const WeekDayHeader = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: theme.spacing(1)
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(1)
}));

const CalendarDay = styled(Paper)(({ theme, iscurrentmonth, isselected, status, isfuture }) => ({
  padding: theme.spacing(1.5),
  minHeight: '110px',
  cursor: isfuture ? 'default' : 'pointer',
  backgroundColor: !iscurrentmonth ? '#f8f9fa' : isfuture ? '#fafafa' : '#ffffff',
  border: isselected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e9ecef',
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  position: 'relative',
  opacity: isfuture ? 0.8 : 1,
  '&:hover': {
    transform: isfuture ? 'none' : 'translateY(-3px)',
    boxShadow: isfuture ? 'none' : theme.shadows[4],
    backgroundColor: isfuture ? '#fafafa' : '#f8f9fa'
  },
  ...(status === 'present' && iscurrentmonth && !isfuture && {
    backgroundColor: '#e8f5e9',
    '&:hover': { backgroundColor: '#c8e6c9' }
  }),
  ...(status === 'late' && iscurrentmonth && !isfuture && {
    backgroundColor: '#fff3e0',
    '&:hover': { backgroundColor: '#ffe0b2' }
  }),
  ...(status === 'absent' && iscurrentmonth && !isfuture && {
    backgroundColor: '#ffebee',
    '&:hover': { backgroundColor: '#ffcdd2' }
  }),
  ...(status === 'holiday' && iscurrentmonth && {
    backgroundColor: '#f3e5f5',
    '&:hover': { backgroundColor: '#e1bee7' }
  })
}));

const StatCard = styled(Card)(({ theme, bgcolor }) => ({
  background: bgcolor || '#ffffff',
  borderRadius: '16px',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)'
  }
}));

function EmployeeAttendanceCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [roleInfo, setRoleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [availableYears, setAvailableYears] = useState([]);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error decoding token:", error);
      return null;
    }
  };

  const getEmployeeIdFromToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      // Try to get from user API
      try {
        const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
          withCredentials: true
        });
        
        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);

          try {
            const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (cmpRes.data && cmpRes.data.data) {
              setCompanyConfig({
                dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
              });
            }
          } catch(e) {}

          return response.data.employee_id;
        }
      } catch (err) {
      }
      
      // Fallback to payload
      if (payload.employee_id) {
        setEmployeeId(payload.employee_id);
        return payload.employee_id;
      }
      
      throw new Error('Employee ID not found');
    } catch (err) {
      setError('Failed to fetch user information');
      return null;
    }
  };

  useEffect(() => {
    const init = async () => {
      const empId = await getEmployeeIdFromToken();
      if (empId) {
        fetchCalendarData();
        fetchTrendsData();
        generateYearOptions();
      }
    };
    init();
  }, [currentDate]);

  const generateYearOptions = () => {
    const currentYear = dayjs().tz(companyConfig.timezone).year();
    const years = [];
    for (let i = currentYear - 3; i <= currentYear + 2; i++) {
      years.push(i);
    }
    setAvailableYears(years);
  };

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!employee_id || !company_id) {
        showNotification('Authentication error. Please log in again.', 'error');
        return;
      }

      const year = dayjs(currentDate).tz(companyConfig.timezone).year();
      const month = dayjs(currentDate).tz(companyConfig.timezone).month() + 1;

      const response = await axios.get(
        `${API_URL}/api/employee-track-attendance/calendar/${employee_id}&year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );

      if (response.data.success) {
        setCalendarData(response.data.data.calendar);
        setAttendanceStats(response.data.data.stats);
        setEmployeeInfo(response.data.data.employee);
        setRoleInfo(response.data.data.role);
      }
      setLoading(false);
    } catch (error) {
      //console.error('Error fetching calendar data:', error);
      setLoading(false);
      showNotification('Failed to fetch attendance data', 'error');
    }
  };
// Add these helper functions to your EmployeeAttendanceCalendar.jsx
// Place them before the component definition

// Convert decimal hours to HH:MM format
const formatDecimalHours = (decimalHours) => {
  if (!decimalHours && decimalHours !== 0) return '0:00';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  if (minutes === 60) {
    return `${hours + 1}:00`;
  }
  
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

// Format time duration from minutes to HH:MM
const formatMinutesToHours = (minutes) => {
  if (!minutes && minutes !== 0) return '0:00';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  return `${hours}:${mins.toString().padStart(2, '0')}`;
};

// Calculate total hours between two times
const calculateTotalHours = (inTime, outTime) => {
  if (!inTime || !outTime) return 0;
  
  const [inHour, inMin] = inTime.split(':').map(Number);
  const [outHour, outMin] = outTime.split(':').map(Number);
  
  let inMinutes = inHour * 60 + inMin;
  let outMinutes = outHour * 60 + outMin;
  
  // Handle overnight shifts
  if (outMinutes < inMinutes) {
    outMinutes += 24 * 60;
  }
  
  const totalMinutes = outMinutes - inMinutes;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return {
    decimal: totalMinutes / 60,
    formatted: `${hours}:${minutes.toString().padStart(2, '0')}`,
    hours: hours,
    minutes: minutes,
    totalMinutes: totalMinutes
  };
};
  const fetchTrendsData = async () => {
    try {
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!employee_id || !company_id) return;

      const response = await axios.get(
        `${API_URL}/api/employee-track-attendance/trends/${employee_id}&months=6`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            
          }
        }
      );

      if (response.data.success) {
        setTrendsData(response.data.data);
      }
    } catch (error) {
      //console.error('Error fetching trends:', error);
    }
  };

  const fetchDayDetails = async (date) => {
    try {
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!employee_id || !company_id) return;

      const response = await axios.get(
        `${API_URL}/api/employee-track-attendance/day-details/${employee_id}/${date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );

      if (response.data.success) {
        setSelectedDayData(response.data.data);
      }
    } catch (error) {
      //console.error('Error fetching day details:', error);
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const changeMonth = (increment) => {
    const newDate = dayjs(currentDate).tz(companyConfig.timezone).add(increment, 'month').date(1).toDate();
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedDayData(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
    setSelectedDayData(null);
  };

  const handleDateSelect = () => {
    const newDate = dayjs(currentDate).tz(companyConfig.timezone).year(selectedYear).month(selectedMonth - 1).date(1).toDate();
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedDayData(null);
    setOpenDatePicker(false);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    const newDate = dayjs(currentDate).tz(companyConfig.timezone).year(year).date(1).toDate();
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedDayData(null);
    setOpenDatePicker(false);
  };

  const handleDayClick = (dayData) => {
    // Don't allow clicking on future dates (dates after today)
    const today = dayjs().tz(companyConfig.timezone).startOf('day');
    const clickDate = dayjs(dayData.date).tz(companyConfig.timezone).startOf('day');
    
    // Only disable if date is strictly greater than today (future dates)
    if (clickDate.isAfter(today)) {
      showNotification('Cannot view details for future dates', 'info');
      return;
    }
    
    // Allow clicking on today and past dates
    setSelectedDate(dayData.date);
    setSelectedDayData(null);
    fetchDayDetails(dayData.date);
  };

// Replace your existing formatTime function with this:
const formatTime = (time) => {
  if (!time) return '-';
  if (typeof time === 'number') {
    return formatDecimalHours(time);
  }
  // If time is in HH:MM format, return as is
  if (time.includes(':')) {
    return time;
  }
  return time;
};

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />;
      case 'late': return <Warning sx={{ fontSize: 18, color: '#ff9800' }} />;
      case 'absent': return <Cancel sx={{ fontSize: 18, color: '#f44336' }} />;
      case 'holiday': return <HolidayVillage sx={{ fontSize: 18, color: '#9c27b0' }} />;
      default: return <EventNote sx={{ fontSize: 18, color: '#9e9e9e' }} />;
    }
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Prepare pie chart data
  const pieData = attendanceStats ? [
    { name: 'Present', value: attendanceStats.present, color: '#4caf50' },
    { name: 'Late', value: attendanceStats.late, color: '#ff9800' },
    { name: 'Absent', value: attendanceStats.absent, color: '#f44336' },
    { name: 'Holiday', value: attendanceStats.holiday, color: '#9c27b0' }
  ] : [];

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Fade in timeout={500}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap">
            <Box>
              <Typography variant="h4" gutterBottom fontWeight="bold">
              Attendance Calendar
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {employeeInfo?.firstName} {employeeInfo?.lastName} • {employeeInfo?.employee_id}
              </Typography>
            </Box>
            {roleInfo && (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Expected Schedule
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {roleInfo.loginTime} - {roleInfo.logoutTime}
                </Typography>
                <Chip 
                  label={roleInfo.shiftType || roleInfo.roleType || 'Regular Shift'} 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 0.5 }}
                />
              </Paper>
            )}
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard bgcolor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                <CardContent>
                  <Typography color="rgba(255,255,255,0.9)" variant="caption">
                    Attendance Rate
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="white">
                    {attendanceStats?.attendanceRate || 0}%
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard bgcolor="#e8f5e9">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" variant="caption">Present</Typography>
                      <Typography variant="h4" fontWeight="bold" color="#2e7d32">
                        {attendanceStats?.present || 0}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: '#4caf50' }} />
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard bgcolor="#fff3e0">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" variant="caption">Late</Typography>
                      <Typography variant="h4" fontWeight="bold" color="#e65100">
                        {attendanceStats?.late || 0}
                      </Typography>
                    </Box>
                    <Warning sx={{ fontSize: 40, color: '#ff9800' }} />
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard bgcolor="#ffebee">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" variant="caption">Absent</Typography>
                      <Typography variant="h4" fontWeight="bold" color="#c62828">
                        {attendanceStats?.absent || 0}
                      </Typography>
                    </Box>
                    <Cancel sx={{ fontSize: 40, color: '#f44336' }} />
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard bgcolor="#f3e5f5">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" variant="caption">Holidays</Typography>
                      <Typography variant="h4" fontWeight="bold" color="#6a1b9a">
                        {attendanceStats?.holiday || 0}
                      </Typography>
                    </Box>
                    <HolidayVillage sx={{ fontSize: 40, color: '#9c27b0' }} />
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Attendance Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                  6-Month Trend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="attendanceRate" name="Attendance Rate %" stroke="#8884d8" fill="#8884d8" />
                    <Area type="monotone" dataKey="present" name="Present Days" stroke="#82ca9d" fill="#82ca9d" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Legend */}
          <Box display="flex" justifyContent="flex-end" mb={2} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
              <Typography variant="caption">Present</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ fontSize: 16, color: '#ff9800' }} />
              <Typography variant="caption">Late</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Cancel sx={{ fontSize: 16, color: '#f44336' }} />
              <Typography variant="caption">Absent</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <HolidayVillage sx={{ fontSize: 16, color: '#9c27b0' }} />
              <Typography variant="caption">Holiday</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <EventNote sx={{ fontSize: 16, color: '#9e9e9e' }} />
              <Typography variant="caption">Upcoming/Future</Typography>
            </Box>
          </Box>

       {/* Calendar Navigation with Date Picker */}
<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
  <IconButton onClick={() => changeMonth(-1)} sx={{ bgcolor: '#f5f5f5' }}>
    <ChevronLeft />
  </IconButton>
  <Box textAlign="center">
    <Button
      variant="outlined"
      startIcon={<CalendarToday />}
      onClick={() => setOpenDatePicker(true)}
      sx={{ borderRadius: 2, px: 3, py: 1 }}
    >
      <Typography variant="h5" fontWeight="bold">
        {dayjs(currentDate).tz(companyConfig.timezone).format('MMMM YYYY')}
      </Typography>
    </Button>
    <Typography variant="body2" color="textSecondary" mt={0.5}>
      {attendanceStats?.workedDays || 0} days worked / {attendanceStats?.totalDays || 0} total days
    </Typography>
  </Box>
  <Box display="flex" alignItems="center" gap={1}>
    {/* Add the Request Correction Button here */}
    <Button
      variant="contained"
      startIcon={<EventNote />}
      onClick={() => setOpenRequestDialog(true)}
      sx={{ 
        bgcolor: '#ff9800', 
        '&:hover': { bgcolor: '#f57c00' },
        borderRadius: 2
      }}
    >
      Request Correction
    </Button>
    <IconButton onClick={goToToday} sx={{ bgcolor: '#f5f5f5' }}>
      <Today />
    </IconButton>
    <IconButton onClick={() => changeMonth(1)} sx={{ bgcolor: '#f5f5f5' }}>
      <ChevronRight />
    </IconButton>
  </Box>
</Box>

          <Grid container spacing={3}>
            {/* Calendar View */}
            <Grid item xs={12} md={8}>
              <CalendarContainer>
                <WeekDayHeader>
                  {weekDays.map(day => (
                    <Typography key={day} variant="subtitle2" align="center" fontWeight={600}>
                      {day}
                    </Typography>
                  ))}
                </WeekDayHeader>
                <CalendarGrid>
                  {calendarData.map((day, index) => {
                    const today = dayjs().tz(companyConfig.timezone).startOf('day');
                    const currentDateObj = dayjs(day.date).tz(companyConfig.timezone).startOf('day');
                    
                    // Check if date is in the future (after today)
                    const isFutureDate = currentDateObj.isAfter(today);
                    
                    // For future dates, only show holidays, not absent status
                    let displayStatus = day.status;
                    let isClickable = !isFutureDate;
                    
                    if (isFutureDate && day.status === 'absent') {
                      displayStatus = 'future';
                    }
                    
                    return (
                      <Zoom in key={index} style={{ transitionDelay: `${index * 10}ms` }}>
                        <CalendarDay
                          iscurrentmonth={true}
                          isselected={selectedDate === day.date}
                          status={displayStatus}
                          isfuture={isFutureDate && displayStatus !== 'holiday'}
                          onClick={() => isClickable && handleDayClick(day)}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              sx={{
                                ...(day.isHoliday && { color: '#9c27b0' }),
                                ...(day.isWeekend && !day.isHoliday && { color: '#f44336' }),
                                ...(isFutureDate && !day.isHoliday && { color: '#9e9e9e' })
                              }}
                            >
                              {day.day}
                            </Typography>
                            {!isFutureDate && (
                              <Badge badgeContent={day.statusIcon} sx={{ '& .MuiBadge-badge': { fontSize: 14 } }} />
                            )}
                            {isFutureDate && day.isHoliday && (
                              <Badge badgeContent="🎉" sx={{ '& .MuiBadge-badge': { fontSize: 14 } }} />
                            )}
                          </Box>
                          {day.attendance?.in_time && !isFutureDate && (
                            <Tooltip title={`In: ${formatTime(day.attendance.in_time)}`}>
                              <Box sx={{ mt: 1, fontSize: '0.7rem', color: 'text.secondary' }}>
                                <AccessTime sx={{ fontSize: 12 }} />
                                {' '}{formatTime(day.attendance.in_time)}
                              </Box>
                            </Tooltip>
                          )}
                          {day.isHoliday && (
                            <Tooltip title={day.holiday?.title || 'Holiday'}>
                              <Box sx={{ mt: 1, fontSize: '0.7rem', color: '#9c27b0' }}>
                                <HolidayVillage sx={{ fontSize: 12 }} />
                                {' '}{day.holiday?.title?.substring(0, 10) || 'Holiday'}
                              </Box>
                            </Tooltip>
                          )}
                          {isFutureDate && !day.isHoliday && (
                            <Box sx={{ mt: 1, fontSize: '0.7rem', color: '#9e9e9e' }}>
                              <EventNote sx={{ fontSize: 12 }} />
                              {' Upcoming'}
                            </Box>
                          )}
                        </CalendarDay>
                      </Zoom>
                    );
                  })}
                </CalendarGrid>
              </CalendarContainer>
            </Grid>

            {/* Attendance Details Panel */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: '20px', height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Day Details
                </Typography>
                
                {selectedDate && selectedDayData ? (
                  <Box mt={2}>
                    <Typography variant="subtitle1" color="primary" gutterBottom fontWeight="bold">
                      {dayjs(selectedDate).tz(companyConfig.timezone).format('dddd, MMMM D, YYYY')}
                    </Typography>
                    
                    {selectedDayData.isHoliday ? (
                      <Box mt={3} textAlign="center" py={4}>
                        <HolidayVillage sx={{ fontSize: 64, color: '#9c27b0', mb: 2 }} />
                        <Typography variant="h6" color="#9c27b0" gutterBottom>
                          🎉 Holiday! 🎉
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {selectedDayData.holiday?.title || 'Company Holiday'}
                        </Typography>
                        {selectedDayData.holiday?.description && (
                          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                            {selectedDayData.holiday.description}
                          </Typography>
                        )}
                      </Box>
                    ) : selectedDayData.attendance ? (
                      <Box mt={2}>
                        <Box display="flex" alignItems="center" mb={2} p={2} bgcolor="#f5f5f5" borderRadius={2}>
                          {getStatusIcon(selectedDayData.attendance.status)}
                          <Typography variant="body1" ml={1} fontWeight="bold">
                            Status: {selectedDayData.attendance.status?.toUpperCase()}
                          </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box>
                          <Box display="flex" alignItems="center" mb={2}>
                            <AccessTime sx={{ mr: 2, color: '#666' }} />
                            <Box>
                              <Typography variant="body2" color="textSecondary">Check In</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {formatTime(selectedDayData.attendance.in_time) || 'Not recorded'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mb={2}>
                            <AccessTime sx={{ mr: 2, color: '#666' }} />
                            <Box>
                              <Typography variant="body2" color="textSecondary">Check Out</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {formatTime(selectedDayData.attendance.out_time) || 'Not recorded'}
                              </Typography>
                            </Box>
                          </Box>
                          
                      {/* Replace the total hours display section (around line 600-610) */}
<Box display="flex" alignItems="center" mb={2}>
  <Schedule sx={{ mr: 2, color: '#666' }} />
  <Box>
    <Typography variant="body2" color="textSecondary">Total Hours</Typography>
    <Typography variant="h6" fontWeight="bold">
      {selectedDayData.attendance.total_hours 
        ? (() => {
            const hours = calculateTotalHours(
              selectedDayData.attendance.in_time, 
              selectedDayData.attendance.out_time
            );
            return hours.formatted;
          })()
        : '0:00'}
    </Typography>
  </Box>
</Box>
                        </Box>

                        {selectedDayData.attendance.status?.toLowerCase() === 'late' && roleInfo && (
                          <Box mt={2} p={2} bgcolor="#fff3e0" borderRadius={2}>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              <Warning sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              Late Analysis
                            </Typography>
                            <Typography variant="body2">
                              Expected: {roleInfo.loginTime}
                            </Typography>
                            <Typography variant="body2">
                              Actual: {formatTime(selectedDayData.attendance.in_time)}
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={100} 
                              sx={{ mt: 1, bgcolor: '#ffe0b2', '& .MuiLinearProgress-bar': { bgcolor: '#ff9800' } }} 
                            />
                          </Box>
                        )}

                        {selectedDayData.attendance.in_latitude && selectedDayData.attendance.in_longitude && (
                          <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={2}>
                            <Typography variant="subtitle2" color="primary.main" gutterBottom>
                              <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                              Check-in Location
                            </Typography>
                            <Typography variant="caption">
                              Lat: {selectedDayData.attendance.in_latitude}, 
                              Long: {selectedDayData.attendance.in_longitude}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box mt={3} textAlign="center" py={4}>
                        <WorkOff sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                        <Typography variant="h6" color="error" gutterBottom>
                          Absent
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          No attendance record found for this date.
                        </Typography>
                        {selectedDayData.isWeekend && (
                          <Chip label="Weekend" size="small" sx={{ mt: 2 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                ) : selectedDate && !selectedDayData ? (
                  <Box mt={3} textAlign="center" py={4}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="textSecondary" mt={2}>
                      Loading details...
                    </Typography>
                  </Box>
                ) : (
                  <Box mt={3} textAlign="center" py={4}>
                    <CalendarMonth sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="body2" color="textSecondary">
                      Click on any date to view attendance details
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                      (Future dates cannot be clicked)
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Date Picker Dialog - Month & Year Selection */}
      <Dialog open={openDatePicker} onClose={() => setOpenDatePicker(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DateRange />
            <Typography variant="h6">Select Month & Year</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Year Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom color="textSecondary">
                Select Year
              </Typography>
              <Grid container spacing={1}>
                {availableYears.map(year => (
                  <Grid item xs={3} key={year}>
                    <Button
                      variant={selectedYear === year ? "contained" : "outlined"}
                      onClick={() => setSelectedYear(year)}
                      fullWidth
                      sx={{ borderRadius: 2 }}
                    >
                      {year}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Month Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom color="textSecondary" sx={{ mt: 1 }}>
                Select Month
              </Typography>
              <Grid container spacing={1}>
                {monthOptions.map(month => (
                  <Grid item xs={4} sm={3} key={month.value}>
                    <Button
                      variant={selectedMonth === month.value ? "contained" : "outlined"}
                      onClick={() => setSelectedMonth(month.value)}
                      fullWidth
                      sx={{ borderRadius: 2, py: 1 }}
                    >
                      {month.label.substring(0, 3)}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDatePicker(false)}>Cancel</Button>
          <Button onClick={handleDateSelect} variant="contained" color="primary">
            View Calendar
          </Button>
        </Box>
      </Dialog>
<EmployeeAttendanceRequest
  open={openRequestDialog}
  onClose={() => setOpenRequestDialog(false)}
  onSuccess={() => {
    fetchCalendarData();
    fetchTrendsData();
  }}
  selectedDate={selectedDate ? dayjs(selectedDate).tz(companyConfig.timezone).toDate() : null}
/>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} elevation={6}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default EmployeeAttendanceCalendar;